/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
import Router from "vue-router";
// import { Store } from 'vuex'
import store from "@/store/store";
import constants from "@/constants";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: "",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: "/organization/my-organizations",
          name: "my-organizations",
          component: () => import("@/views/loading/LoadingPage.vue"),
        },
        {
          path: '/claim-profile',
          name: 'ClaimProfile',
          component: () => import('@/views/ClaimProfile.vue'), 
          props: (route) => ({ token: route.query.token })
       },
       {
          path: '/opt-out',
          name: 'OptOut',
          component: () => import('@/views/OptOut.vue'), 
          props: (route) => ({ token: route.query.token })
       },
        {
          path: "/login",
          name: "login",
          component: () => import("@/views/auth/Login.v2.vue"),
        },

        {
          path: "/oauth-callback",
          name: "oauth-callback",
          component: () => import("@/views/auth/OauthCallback.vue"),
        },
        {
          path: '/organization/:slug/talent-plans',
          name: 'org-talent-plans',
          component: () => import('@/views/org-dashboard/users/components/UserRecommendations.vue'), // Adjust path if needed
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Talent Recommendations', active: true },
            ],
            pageTitle: 'Talent Recommendations',
            rule: 'editor', 
          },
        },

        {
          path: "/",
          name: "home",
          component: () => import("@/views/home/Home.v1.vue"),
          beforeEnter: (to, from, next) => {
            window.location.href = "https://www.getboarded.com/";
          },
        },

        {
          path: "/login/board/invite/:invitePhrase",
          name: "board-invite-auto-login",
          component: () => import("@/views/auth/LoginLink.vue"),
        },
        {
          path: "/login/magic/:invitePhrase",
          name: "magic-link-auto-login",
          component: () => import("@/views/auth/MagicLink.vue"),
        },

        {
          path: "/board/invite/:boardSlug",
          name: "link-board-invite",
          component: () =>
            import("@/views/user-dashboard/boards/LinkBoardInvite.vue"),
        },
        {
          path: "/home",
          name: "home-page",
          component: () => import("@/views/home/Home.v1.vue"),
        },
        {
          path: "/thankyou",
          name: "thankyou",
          component: () => import("./views/thankyou.vue"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: "/locked-profile",
          name: "locked-profile",
          component: () =>
            import("@/layouts/components/editor/LockedProfile.vue"),
          meta: {
            rule: "isPublic",
          },
        },

        {
          path: "/about-us",
          name: "about-us",
          component: () => import("./views/pages/about/about-us-new/Main"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: "/contact-us",
          name: "contact-us",
          component: () => import("./views/pages/about/about-us-new/Main"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: "/error-404",
          name: "page-error-404",
          component: () => import("@/views/pages/Error404.vue"),
        },

        {
          path: "/journey",
          name: "JourneyLanding",
          component: () => import("./views/pages/journey/Home.vue"),
          meta: {
            rule: "isPublic",
          },
        },
      ],
    },

    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: "",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        // {
        //   path: '/',
        //   name: 'home',
        //   component: () => import('./views/pages/home/Main'),
        //   meta: {
        //     rule: 'isPublic'
        //   }
        // },
        {
          path: "/university",
          name: "university",
          component: () => import("./views/pages/university/Main"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: "/company",
          name: "company",
          component: () => import("./views/pages/company/Main"),
          meta: {
            rule: "isPublic",
          },
        },

        // {
        //   path: '/about-us',
        //   name: 'about-us',
        //   component: () => import('./views/pages/about/about-us/Main'),
        //   meta: {
        //     rule: 'isPublic'
        //   }
        // },
        // {
        //   path: '/contact-us',
        //   name: 'contact-us',
        //   component: () => import('./views/pages/about/contact-us/Main'),
        //   meta: {
        //     rule: 'isPublic'
        //   }
        // },
        {
          path: "/privacy-policy",
          name: "privacy-policy",
          component: () => import("./views/pages/about/privacy-policy/Main"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: "/youlab/identify-competencies",
          name: "identify-competencies",
          component: () =>
            import("./views/pages/about/identify_competencies/Main"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: "/gdpr-compliance",
          name: "gdpr-compliance",
          component: () => import("./views/pages/about/gdpr-compliance/Main"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: "/cookie-policy",
          name: "cookie-policy",
          component: () => import("./views/pages/about/cookie-policy/Main"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: "/terms-of-use",
          name: "terms-of-use",
          component: () => import("./views/pages/about/terms-of-use/Main"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: "/:boardSlug/:boardUserId/parser",
          name: "parser-custom",
          component: () => import("./views/pages/parser/Main"),
          meta: {
            // rule: 'isPublic'
          },
        },
        {
          path: "/parser",
          name: "parser",
          component: () => import("./views/pages/parser/Main"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: "/personality-report",
          name: "personality-report",
          component: () => import("./views/pages/personality-report/Main"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: "/competency-report",
          name: "competency-report",
          component: () => import("./views/pages/competency-report/Main"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: ":organizationSlug/alex",
          component: () => import("./views/pages/alex/Index.vue"),
          children: [
            {
              path: "",
              name: "alex-dpd",
              component: () => import("./views/pages/alex/landing/Main"),
              meta: {
                rule: "isPublic",
                title: "MyPath - Adaptive Learning EXperience (ALEX)",
              },
            },
            {
              path: "home",
              name: "alex-home",
              component: () => import("./views/pages/alex/Main/Main"),
              meta: {
                rule: "isPublic",
                title: "MyPath - Adaptive Learning EXperience (ALEX)",
              },
            },
            {
              path: "curate",
              name: "alex-curate",
              component: () => import("./views/pages/alex/curate/Main"),
              meta: {
                rule: "isPublic",
                title: "Curate | MyPath - Adaptive Learning EXperience (ALEX)",
              },
            },
            {
              path: "customize",
              name: "alex-customize",
              component: () => import("./views/pages/alex/customize/Main"),
              meta: {
                rule: "isPublic",
                title:
                  "Customize | MyPath - Adaptive Learning EXperience (ALEX)",
              },
            },
            {
              path: "connect",
              name: "alex-connect",
              component: () => import("./views/pages/alex/connect/Main"),
              meta: {
                rule: "isPublic",
                title: "Connect | MyPath - Adaptive Learning EXperience (ALEX)",
              },
            },
            {
              path: "assessment",
              name: "alex-assessment",
              component: () => import("./views/pages/alex/assessment/Main"),
              meta: {
                rule: "isPublic",
                title:
                  "Assessment | MyPath - Adaptive Learning EXperience (ALEX)",
              },
            },
            {
              path: "feedback",
              name: "alex-feedback",
              component: () => import("./views/pages/alex/feedback/Main"),
              meta: {
                rule: "isPublic",
                title:
                  "My Advisor | MyPath - Adaptive Learning EXperience (ALEX)",
              },
            },
            {
              path: "identify_competencies",
              name: "identify_competencies",
              component: () =>
                import("./views/pages/alex/identify_competencies/Main"),
              meta: {
                rule: "isPublic",
                title:
                  "My Advisor | MyPath - Adaptive Learning EXperience (ALEX)",
              },
            },

            {
              path: "library",
              name: "alex-library",
              component: () => import("./views/pages/alex/library/Main"),
              meta: {
                rule: "isPublic",
                title:
                  "My Library | MyPath - Adaptive Learning EXperience (ALEX)",
              },
            },
          ],
        },

        {
          path: "gala/:boardSlug/:userId/journey",
          component: () => import("./views/pages/gala-games/Index.vue"),
          children: [
            {
              path: "",
              name: "gala-games",
              component: () => import("./views/pages/gala-games/landing/Main"),
              meta: {
                rule: "isPublic",
                title: "Journey",
              },
            },
            {
              path: "assessment",
              name: "gala-assessment",
              component: () =>
                import("./views/pages/gala-games/assessment/Main"),
              meta: {
                rule: "isPublic",
                title: "Assessment | Journey ",
              },
            },
            {
              path: "feedback",
              name: "gala-feedback",
              component: () => import("./views/pages/gala-games/feedback/Main"),
              meta: {
                rule: "isPublic",
                title: "My Advisor | Journey ",
              },
            },
            {
              path: "identify_competencies",
              name: "identify_competencies",
              component: () =>
                import("./views/pages/gala-games/identify_competencies/Main"),
              meta: {
                rule: "isPublic",
                title: "My Advisor | Journey",
              },
            },
          ],
        },
        {
          path: "gala/journey/",
          component: () => import("./views/pages/gala-games/Index.vue"),
          children: [
            {
              path: "",
              name: "gala-games",
              component: () => import("./views/pages/gala-games/landing/Main"),
              meta: {
                rule: "isPublic",
                title: "Journey ",
              },
            },
            {
              path: "assessment",
              name: "gala-assessment",
              component: () =>
                import("./views/pages/gala-games/assessment/Main"),
              meta: {
                rule: "isPublic",
                title: "Assessment | Journey ",
              },
            },
            {
              path: "feedback",
              name: "gala-feedback",
              component: () => import("./views/pages/gala-games/feedback/Main"),
              meta: {
                rule: "isPublic",
                title: "My Advisor | Journey ",
              },
            },
            {
              path: "identify_competencies",
              name: "identify_competencies",
              component: () =>
                import("./views/pages/gala-games/identify_competencies/Main"),
              meta: {
                rule: "isPublic",
                title: "My Competencies | Journey ",
              },
            },
            {
              path: "library",
              name: "gala-library",
              component: () => import("./views/pages/gala-games/library/Main"),
              meta: {
                rule: "isPublic",
                title:
                  "My Library | Journey - Adaptive Learning EXperience (ALEX)",
              },
            },
            {
              path: "situations",
              name: "situations",
              component: () =>
                import("./views/pages/gala-games/situations/Main"),
              meta: {
                rule: "isPublic",
                title: "My situations | Journey",
              },
            },
            {
              path: "plan",
              name: "plan",
              component: () => import("./views/pages/gala-games/plan/Main"),
              meta: {
                rule: "isPublic",
                title: "My Plan | Journey",
              },
            },
          ],
        },
        {
          path: "emotionhack/:boardSlug/:userId/journey",
          component: () => import("./views/pages/emotionhack/Index.vue"),
          children: [
            {
              path: "",
              name: "emotionhack",
              component: () => import("./views/pages/emotionhack/landing/Main"),
              meta: {
                rule: "isPublic",
                title: "Journey ",
              },
            },
            {
              path: "assessment",
              name: "gala-assessment",
              component: () =>
                import("./views/pages/emotionhack/assessment/Main"),
              meta: {
                rule: "isPublic",
                title: "Assessment | EmotionHack ",
              },
            },
            {
              path: "feedback",
              name: "gala-feedback",
              component: () =>
                import("./views/pages/emotionhack/feedback/Main"),
              meta: {
                rule: "isPublic",
                title: "My Advisor | EmotionHack ",
              },
            },
            {
              path: "identify_competencies",
              name: "identify_competencies",
              component: () =>
                import("./views/pages/emotionhack/identify_competencies/Main"),
              meta: {
                rule: "isPublic",
                title: "My Competencies | EmotionHack ",
              },
            },
            {
              path: "library",
              name: "emotionhack-library",
              component: () => import("./views/pages/emotionhack/library/Main"),
              meta: {
                rule: "isPublic",
                title:
                  "My Library | EmotionHack - Adaptive Learning EXperience (ALEX)",
              },
            },
            {
              path: "situations",
              name: "situations",
              component: () =>
                import("./views/pages/emotionhack/situations/Main"),
              meta: {
                rule: "isPublic",
                title: "My situations | EmotionHack",
              },
            },
            {
              path: "plan",
              name: "plan",
              component: () => import("./views/pages/emotionhack/plan/Main"),
              meta: {
                rule: "isPublic",
                title: "My Plan | EmotionHack",
              },
            },
          ],
        },
        {
          path: "emotionhack/journey",
          component: () => import("./views/pages/emotionhack/Index.vue"),
          children: [
            {
              path: "",
              name: "emotionhack",
              component: () => import("./views/pages/emotionhack/landing/Main"),
              meta: {
                rule: "isPublic",
                title: "Journey ",
              },
            },
            {
              path: "assessment",
              name: "gala-assessment",
              component: () =>
                import("./views/pages/emotionhack/assessment/Main"),
              meta: {
                rule: "isPublic",
                title: "Assessment | EmotionHack ",
              },
            },
            {
              path: "feedback",
              name: "gala-feedback",
              component: () =>
                import("./views/pages/emotionhack/feedback/Main"),
              meta: {
                rule: "isPublic",
                title: "My Advisor | EmotionHack ",
              },
            },
            {
              path: "identify_competencies",
              name: "identify_competencies",
              component: () =>
                import("./views/pages/emotionhack/identify_competencies/Main"),
              meta: {
                rule: "isPublic",
                title: "My Competencies | EmotionHack ",
              },
            },
            {
              path: "library",
              name: "emotionhack-library",
              component: () => import("./views/pages/emotionhack/library/Main"),
              meta: {
                rule: "isPublic",
                title:
                  "My Library | EmotionHack - Adaptive Learning EXperience (ALEX)",
              },
            },
            {
              path: "situations",
              name: "situations",
              component: () =>
                import("./views/pages/emotionhack/situations/Main"),
              meta: {
                rule: "isPublic",
                title: "My situations | EmotionHack",
              },
            },
            {
              path: "plan",
              name: "plan",
              component: () => import("./views/pages/emotionhack/plan/Main"),
              meta: {
                rule: "isPublic",
                title: "My Plan | EmotionHack",
              },
            },
          ],
        },
        {
          path: "emotionhack/library",
          name: "emotionhack-library",
          component: () => import("./views/pages/emotionhack/library/Main"),
          meta: {
            rule: "isPublic",
            title:
              "My Library | EmotionHack - Adaptive Learning EXperience (ALEX)",
          },
        },
        {
          path: "gala/library",
          name: "gala-library",
          component: () => import("./views/pages/gala-games/library/Main"),
          meta: {
            rule: "isPublic",
            title: "My Library | Journey - Adaptive Learning EXperience (ALEX)",
          },
        },
        {
          path: "/integrations",
          name: "integrations",
          component: () => import("./views/pages/integrations/Main"),
          meta: {
            rule: "isPublic",
          },
        },

        {
          path: "/integrations/marketplace",
          name: "integrations-marketplace",
          component: () =>
            import("./views/pages/integrations/marketplace/Main"),
          meta: {
            rule: "isPublic",
          },
        },
      ],
    },
    {
      // =============================================================================
      // ADMIN LAYOUT ROUTES
      // =============================================================================
      path: "",
      component: () => import("./layouts/main/Admin.vue"),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: "/admin",
          name: "admin-main",
          component: () => import("./views/admin/Main.vue"),
          meta: {
            rule: "isPublic",
          },
        },
        {
          path: "/admin/users",
          name: "admin-users",
          component: () => import("./views/admin/Main.vue"),
          meta: {
            rule: "isPublic",
          },
        },
      ],
    },
    {
      // =============================================================================
      // ORGANIZATION DASHBOARD LAYOUT ROUTES
      // =============================================================================
      path: "",
      component: () => import("@/layouts/main/NewOrganizationDashboard.vue"),
      children: [
        // {
        //   path: "/my-organizations",
        //   name: "my-organizations",
        //   component: () => import("@/views/org-dashboard/Main.vue"),
        //   meta: {
        //     title: "My Organizations",
        //     pageTitle: "My Organizations",
        //     authenticationRequired: true,
        //   },
        // },
        {
          path: "/organization/:organizationId/dashboard",
          name: "org-home",
          component: () => import("@/views/org-dashboard/Organization.vue"),
          meta: {
            title: "Organization Dashboard",
            breadcrumb: [{ title: "Dashboard", active: true }],
            pageTitle: "Dashboard",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/boards",
          name: "org-boards",
          component: () => import("@/views/org-dashboard/boards/Main.vue"),
          meta: {
            title: "Organization Boards",
            breadcrumb: [{ title: "Boards", active: true }],
            pageTitle: "Boards",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/new-boards",
          name: "org-boards",
          component: () => import("@/views/org-dashboard/new-boards/Main.vue"),
          meta: {
            title: "Organization Boards",
            breadcrumb: [{ title: "Boards", active: true }],
            pageTitle: "Boards",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/reviews",
          name: "org-reviews",
          component: () => import("@/views/org-dashboard/reviews/Main.vue"),
          meta: {
            title: "Organization Reviews",
            breadcrumb: [{ title: "Home" }, { title: "Reviews", active: true }],
            pageTitle: "Reviews",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/reviews/users-done-items",
          name: "org-users-done-items-reviews",
          component: () =>
            import(
              "@/views/org-dashboard/reviews/assignment-review/UsersDoneItem.vue"
            ),
          meta: {
            title: "Organization Done Items",
            pageTitle: "Reviews",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/review/:itemId",
          name: "org-assignment-review",
          component: () =>
            import(
              "@/views/org-dashboard/reviews/assignment-review/ReviewPage.vue"
            ),
          meta: {
            title: "Review",
            pageTitle: "Review",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/review",
          name: "single-user-org-assignment-review",
          component: () =>
            import(
              "@/views/org-dashboard/reviews/assignment-review/SingleUserAssignments.vue"
            ),
          meta: {
            title: "Review",
            pageTitle: "Review",
            authenticationRequired: true,
          },
          beforeEnter(to, from, next) {
            // Proceed with the route update
            if (!to.query.userId) {
              next({
                name: "page-error-404",
              });
            } else {
              next();
            }
          },
        },
        {
          path: "/organization/:organizationId/settings",
          name: "org-settings",
          component: () => import("@/views/org-dashboard/settings/Main.vue"),
          meta: {
            title: "Organization Settings",
            breadcrumb: [
              { title: "Home" },
              { title: "Settings", active: true },
            ],
            pageTitle: "Settings",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/archived-boards",
          name: "org-archived-boards",
          component: () =>
            import(
              "@/views/org-dashboard/boards/archived-boards/ArchivedBoards.vue"
            ),
          meta: {
            title: "Organization Archived Boards",
            breadcrumb: [
              { title: "Home" },
              { title: "Archived Boards", active: true },
            ],
            pageTitle: "Archived Boards",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/create-board/:boardId",
          name: "board-create-step1",
          component: () =>
            import(
              "@/views/org-dashboard/boards/board-create/manual-flow/Step1.vue"
            ),
          meta: {
            title: "Create Board",
            breadcrumb: [
              { title: "Home" },
              { title: "Create Board", active: true },
            ],
            pageTitle: "Create Board",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/create-board/:boardId/step2",
          name: "board-create-step2",
          component: () =>
            import(
              "@/views/org-dashboard/boards/board-create/manual-flow/Step2.vue"
            ),
          meta: {
            title: "Create Board",
            breadcrumb: [
              { title: "Home" },
              { title: "Create Board", active: true },
            ],
            pageTitle: "Create Board",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/create-board/:boardId/step3",
          name: "board-create-step3",
          component: () =>
            import(
              "@/views/org-dashboard/boards/board-create/manual-flow/Step3.vue"
            ),
          meta: {
            title: "Create Board",
            breadcrumb: [
              { title: "Home" },
              { title: "Create Board", active: true },
            ],
            pageTitle: "Create Board",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/create-board/:boardId/step4",
          name: "board-create-step4",
          component: () =>
            import(
              "@/views/org-dashboard/boards/board-create/manual-flow/Step4.vue"
            ),
          meta: {
            title: "Create Board",
            breadcrumb: [
              { title: "Home" },
              { title: "Create Board", active: true },
            ],
            pageTitle: "Create Board",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/planner",
          name: "org-planner",
          component: () => import("@/views/org-dashboard/planner/Main.vue"),
          meta: {
            title: "Planner",
            breadcrumb: [{ title: "Home" }, { title: "Planner", active: true }],
            pageTitle: "Planner",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/onboarding",
          name: "Onboarding",
          component: () => import("@/views/org-dashboard/onboarding/Main.vue"),
          meta: {
            title: "Onboarding",
            breadcrumb: [{ title: "Home" }, { title: "Planner", active: true }],
            pageTitle: "Onboarding",
            authenticationRequired: true,
          },
        },
        {
        path: "/organization/:organizationId/onboarding/addTalent",
        name: "AddTalent", 
        component: () => import("@/views/org-dashboard/onboarding/Main.vue"),
        meta: {
          title: "Add Talent",
          breadcrumb: [{ title: "Home" }, { title: "Talent Pool", active: true }],
          pageTitle: "Add Talent",
          authenticationRequired: true,
        },
      },
        {
          path: "/organization/:organizationId/messages",
          name: "org-messages",
          component: () => import("@/views/org-dashboard/messages/Main.vue"),
          meta: {
            title: "Organization Messages",
            breadcrumb: [
              { title: "Home" },
              { title: "Messages", active: true },
            ],
            pageTitle: "Messages",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/admins",
          name: "org-admins",
          component: () => import("@/views/org-dashboard/admins/Main.vue"),
          meta: {
            title: "Organization Admins",
            breadcrumb: [{ title: "Home" }, { title: "Admins", active: true }],
            pageTitle: "Admins",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/treasury",
          name: "org-treasury",
          component: () => import("@/views/org-dashboard/treasury/Main.vue"),
          meta: {
            title: "Organization Treasury",
            breadcrumb: [
              { title: "Home" },
              { title: "Treasury", active: true },
            ],
            pageTitle: "Treasury",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/talent-pool",
          name: "org-users",
          component: () => import("@/views/org-dashboard/talent-pool/Main.vue"),
          meta: {
            title: "Organization Talent Pool",
            breadcrumb: [{ title: "Home" }, { title: "Users", active: true }],
            pageTitle: "Talent Pool",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/evaluate",
          name: "org-evaluation",
          component: () => import("@/views/org-dashboard/users/Main.vue"),
          meta: {
            title: "Evaluation",
            breadcrumb: [{ title: "Home" }, { title: "Users", active: true }],
            pageTitle: "Evaluation",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:slug/org-design",
          name: "org-role-design",
          component: () => import("@/views/org-dashboard/role_design/RoleDesign.vue"), // Ensure this path matches where you save the file
          meta: {
            title: "Organization Design",
            breadcrumb: [
              { title: "Organization", active: false },
              { title: "Organization Design", active: true }
            ],
            pageTitle: "Organization Design",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/plans",
          name: "org-plan",
          component: () => import("@/views/org-dashboard/users/components/UserRecommendations.vue"),
          meta: {
            title: "Talent Plans | Organization",
            breadcrumb: [{ title: "Home" }, { title: "Users", active: true }],
            pageTitle: "Talent Plans",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/my-organizations",
          name: "user-organizations",
          component: () =>
            import("@/views/org-dashboard/Main.vue"),
          meta: {
            title: "My Organizations",
            breadcrumb: [{ title: "Home" }],
            pageTitle: "My Organizations",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/request",
          name: "org-joining-users-request",
          component: () =>
            import("@/views/org-dashboard/talent-pool/ReviewRequest.vue"),
          meta: {
            title: "Pending Request",
            breadcrumb: [
              { title: "Home" },
              { title: "Pending Request", active: true },
            ],
            pageTitle: "Pending Request",
            authenticationRequired: true,
          },
        },
        /*{
            path: '/organization/:organizationId/alex',
            name: 'org-marketplace-alex',
            component: () => import('@/views/org-dashboard/alex/Main.vue'),
            meta: {
              title: 'Alex | Organization',
              breadcrumb: [
                { title: 'Home' },
                { title: 'Dashboard' },
                { title: 'Alex', active: true }
              ],
              pageTitle: 'Alex',
              authenticationRequired: true
            }
          },*/
        {
          path: "/organization/:organizationId/marketplace/:marketplaceAppId",
          name: "org-marketplace-alex-detail",
          component: () =>
            import("@/views/org-dashboard/marketplace/Details.vue"),
          meta: {
            title: "Marketplace | Organization",
            breadcrumb: [
              { title: "Home" },
              { title: "Marketplace", active: true },
            ],
            pageTitle: "Marketplace",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/marketplace/config/bot",
          name: "bot",
          component: () =>
            import(
              "@/views/org-dashboard/marketplace/components/ChatBotConfig.vue"
            ),
          meta: {
            title: "Marketplace | Config Bot",
            breadcrumb: [
              { title: "Home" },
              { title: "Marketplace" },
              { title: "Config Bot", active: true },
            ],
            pageTitle: "Config Bot",
            authenticationRequired: false,
          },
        },
        // {
        //   path: "/organization/:organizationId/marketplace/config/marketapp",
        //   name: "market",
        //   component: () =>
        //     import(
        //       "@/views/org-dashboard/marketplace/components/MarketConfig.vue"
        //     ),
        //   meta: {
        //     title: "Marketplace | Market Config",
        //     breadcrumb: [
        //       { title: "Home" },
        //       { title: "Marketplace" },
        //       { title: "Market Config", active: true },
        //     ],
        //     pageTitle: "Market Config",
        //     authenticationRequired: false,
        //   },
        // },
        {
          path: "/organization/:organizationId/marketplace/config/marketapp",
          name: "market",
          component: () =>
            import(
              "@/views/org-dashboard/marketplace/components/MarketConfigV2.vue"
            ),
          meta: {
            title: "Marketplace | Market Config",
            breadcrumb: [
              { title: "Home" },
              { title: "Marketplace" },
              { title: "Market Config", active: true },
            ],
            pageTitle: "Market Config",
            authenticationRequired: false,
          },
        },
        {
          path: "/organization/:organizationId/integrations",
          name: "org-integrations",
          component: () =>
            import("@/views/org-dashboard/integrations/Main.vue"),
          meta: {
            title: "Integrations | Organization",
            breadcrumb: [{ title: "Home" }, { title: "Apps", active: true }],
            pageTitle: "Integrations",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/marketplace",
          name: "org-marketplace",
          component: () => import("@/views/org-dashboard/marketplace/Main.vue"),
          meta: {
            title: "Marketplace | Organization",
            breadcrumb: [{ title: "Home" }, { title: "Apps", active: true }],
            pageTitle: "Apps",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/board-templates",
          name: "org-board-templates",
          component: () =>
            import("@/views/org-dashboard/boards/board-create/Main.vue"),
          meta: {
            title: "Boards | Organization",
            breadcrumb: [
              { title: "Home" },
              { title: "Board Templates", active: true },
            ],
            pageTitle: "Board Templates",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/boards/:boardId/checkout",
          name: "org-board-checkout",
          component: () =>
            import(
              "@/views/org-dashboard/boards/board-create/BoardCheckout.vue"
            ),
          meta: {
            title: "Board Configuration | Organization",
            breadcrumb: [
              { title: "Home" },
              { title: "Board Configuration", active: true },
            ],
            pageTitle: "Board Configuration",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/boards/:boardId",
          name: "org-boards-view",
          component: () =>
            import("@/views/org-dashboard/boards/board-view/Main.vue"),
          meta: {
            title: "Boards | Organization",
            breadcrumb: [{ title: "Home" }, { title: "Boards", active: true }],
            pageTitle: "Board Details",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/boards/:boardId/edit",
          name: "org-boards-edit",
          component: () =>
            import("@/views/org-dashboard/boards/board-edit/Main.vue"),
          meta: {
            title: "Boards | Organization",
            breadcrumb: [
              { title: "Home" },
              { title: "Update Board Configuration", active: true },
            ],
            pageTitle: "Update Board Configuration",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/reports",
          name: "org-reports",
          component: () => import("@/views/org-dashboard/reports/Main.vue"),
          meta: {
            title: "Reports | Organization",
            breadcrumb: [{ title: "Home" }, { title: "Reports", active: true }],
            pageTitle: "Reports",
            authenticationRequired: true,
          },
        },
        {
          path: "/organization/:organizationId/reports/journey",
          name: "org-reports",
          component: () =>
            import("@/views/org-dashboard/reports/apps/Journey/Main.vue"),
          meta: {
            title: "Journey App Report | Organization",
            breadcrumb: [
              { title: "Home" },
              { title: "Reports" },
              { title: "Journey Report", active: true },
            ],
            pageTitle: "Journey App Report",
            authenticationRequired: true,
          },
        },
      ],
    },
    {
      // =============================================================================
      // USER DASHBOARD LAYOUT ROUTES
      // =============================================================================
      path: "",
      component: () => import("@/layouts/main/NewDashboard.vue"),
      children: [
        {
          path: "/organization/create",
          name: "org-create",
          component: () =>
            import("@/views/org-dashboard/createOrganization.vue"),
          meta: {
            title: "Organization Create",
            breadcrumb: [
              { title: "Home" },
              { title: "User Dashboard" },
              { title: "Create Organization", active: true },
            ],
            pageTitle: "Create Organization",
            authenticationRequired: true,
          },
        },
        {
          path: "/user/dashboard",
          name: "user-home",
          component: () => import("@/views/user-dashboard/Main.vue"),
          meta: {
            title: "User Dashboard",
            breadcrumb: [{ title: "User Dashboard", active: true }],
            pageTitle: "Dashboard",
            authenticationRequired: true,
          },
        },
        {
          path: "/user/boards",
          name: "user-boards",
          component: () => import("@/views/user-dashboard/boards/Main.vue"),
          meta: {
            title: "Boards",
            breadcrumb: [{ title: "Boards", active: true }],
            pageTitle: "Boards",
            authenticationRequired: true,
          },
        },
        {
          path: "/user/notifications",
          name: "user-notifications",
          component: () =>
            import("@/views/user-dashboard/notifications/Main.vue"),
          meta: {
            title: "Notifications",
            breadcrumb: [{ title: "Notifications", active: true }],
            pageTitle: "Notifications",
            authenticationRequired: true,
          },
        },
        {
          path: "/user/community",
          name: "user-kvdr",
          component: () =>
            import("@/views/user-dashboard/community/plan/Main.vue"),
          meta: {
            title: "kvdr",
            breadcrumb: [{ title: "kvdr", active: true }],
            pageTitle: "Plan",
            authenticationRequired: true,
          },
        },

        {
          path: "/user/wallet",
          name: "user-wallet",
          component: () => import("@/views/user-dashboard/wallet/Main.vue"),
          meta: {
            title: "User Wallet",
            breadcrumb: [{ title: "Home" }, { title: "Wallet", active: true }],
            pageTitle: "Wallet",
            authenticationRequired: true,
          },
        },
        {
          path: "/user/settings",
          name: "user-settings",
          component: () => import("@/views/user-dashboard/settings/Main.vue"),
          meta: {
            title: "User Settings",
            breadcrumb: [
              { title: "Home" },
              { title: "User Dashboard" },
              { title: "Settings", active: true },
            ],
            pageTitle: "Settings",
            authenticationRequired: true,
          },
        },
        {
          path: "/user/messages",
          name: "user-messages",
          component: () => import("@/views/user-dashboard/messages/Main.vue"),
          meta: {
            title: "User Messages",
            breadcrumb: [
              { title: "Home" },
              { title: "User Dashboard" },
              { title: "Messages", active: true },
            ],
            pageTitle: "Messages",
            authenticationRequired: true,
          },
        },
        {
          path: "/user/marketplace/alex",
          name: "user-marketplace-alex",
          component: () => import("@/views/user-dashboard/alex/Main.vue"),
          meta: {
            title: "Alex | User",
            breadcrumb: [
              { title: "Home" },
              { title: "User Dashboard" },
              { title: "Marketplace" },
              { title: "Alex", active: true },
            ],
            pageTitle: "Alex",
            authenticationRequired: true,
          },
        },
        {
          path: "/user/marketplace",
          name: "user-marketplace",
          component: () => import("@/views/org-dashboard/Main.vue"),
          meta: {
            title: "Marketplace | User",
            breadcrumb: [
              { title: "Home" },
              { title: "User Dashboard" },
              { title: "Marketplace", active: true },
            ],
            pageTitle: "Marketplace",
            authenticationRequired: true,
          },
        },
        {
          path: "/user/board/:boardId",
          name: "user-board",
          component: () =>
            import("@/views/user-dashboard/boards/boards-view/Main.vue"),
          meta: {
            title: "Board | User",
            breadcrumb: [
              { title: "Home" },
              { title: "User Dashboard" },
              { title: "Board", active: true },
            ],
            pageTitle: "Board",
            authenticationRequired: true,
          },
        },
      ],
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: "",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        // {
        //   path: '/login',
        //   name: 'login',
        //   component: () => import('@/views/auth/Login.v2.vue')
        // },

        // {
        //   path: '/login-v1',
        //   name: 'login-v1',
        //   component: () => import('@/views/home/Home.v1.vue')
        // },

        {
          path: "/organization/create/company-profile",
          name: "org-create-company-profile",
          component: () =>
            import("@/views/org-dashboard/onboarding/orgInfo.vue"),
          meta: {
            title: "Setup Company Profile",
            breadcrumb: [{ title: "Home" }],
            pageTitle: "Setup Company Profile",
            authenticationRequired: true,
          },
        },
        {
          path: "/all-organizations",
          name: "all-organizations",
          component: () => import("@/views/pages/AllOrg.vue"),
          meta: {
            title: "All Organizations",
            breadcrumb: [{ title: "Home" }],
            pageTitle: "All Organizations",
            authenticationRequired: true,
          },
        },
        {
          path: "/error-404",
          name: "page-error-404",
          component: () => import("@/views/pages/Error404.vue"),
        },
        {
          path: "/validation-attendance-completed/thanks",
          component: () => import("@/views/pages/validation/ThankYou.vue"),
          meta: {
            rule: "isPublic",
            title: "Thank You",
          },
        },
        {
          path: "/validation-attendance-completed/process",
          component: () => import("@/views/pages/validation/Process.vue"),
          meta: {
            rule: "isPublic",
            title: "Notice",
          },
          // beforeEnter(to, from, next) {
          //   // Proceed with the route update
          //   if (
          //     !to.query.orgId ||
          //     !to.query.itemId ||
          //     !to.query.token
          //   ) {
          //     next({
          //       name: "error-404",
          //     });
          //   } else {
          //     next();
          //   }
          // },
        },
        {
          path: "/validation-attendance-completed/already-given",
          component: () => import("@/views/pages/validation/AlreadyGiven.vue"),
          meta: {
            rule: "isPublic",
            title: "Attendance Submitted",
          },
        },
      ],
    },

    {
      path: "",
      component: () => import("@/layouts/full-page/FullPageResume.vue"),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: "/profile/:userSlug",
          name: "published-resume",
          meta: {
            title: "My Resume | GetBoarded",
          },
        },
        {
          path: ":organizationSlug/profile/:userSlug",
          name: "published-resume-custom",
          meta: {
            title: "My Resume | GetBoarded",
          },
        },
        {
          path: ":boardSlug/:boardUserId/profile/:userSlug",
          name: "published-resume-custom",
          meta: {
            title: "My Resume | GetBoarded",
          },
        },
      ],
    },
    // Redirect to 404 page, if no match found
    {
      path: "*",
      redirect: "/error-404",
    },
  ],
});

router.beforeEach((to, from, next) => {
  // This is temporary, will be removed when home page is ready
  // if(to.name === 'home') {
  //   if(store.state.auth.isUserLoggedIn) {
  //     window.location.href = "/user/dashboard";
  //   } else {
  //     window.location.href = "/login";
  //   }
  // }
  ////////////////////////////////////////////

  // Set Page Title
  const DEFAULT_TITLE = "GetBoarded";
  Vue.nextTick(() => {
    document.title = to.meta.title || DEFAULT_TITLE;
  });

  // // console.log(store.state.auth);
  const isAuthenticated = store.state.auth.isUserLoggedIn;
  // // console.log(isAuthenticated, to.meta.authenticationRequired);
  if (to.meta.authenticationRequired && !isAuthenticated) {
    window.location.href = `${constants.apiHost}/oauth`; // "/login";
  } else {
    return next();
  }
});

router.afterEach((to, from) => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
  // const DEFAULT_TITLE = 'GetBoarded'
  // Vue.nextTick(() => {
  //     document.title = to.meta.title || DEFAULT_TITLE;
  // });
});

export default router;
